import { contentPreferenceRevision, subscribeContentPreferences } from '../../utils/contentPreferences';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { redirectToCallSignIn } from '../../utils/callLinks';
import flashMessage from '../HelperFunctions';
import './TranscriptsView.css';
import CallHistoryItem from '../CallHistoryItem';
import CallDetailDrawer from '../CallDetailDrawer';
import RecordingSettingsModal from '../RecordingSettingsModal';
import { Button, EmptyState, Skeleton, Surface } from '../ui/Primitives';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('jwt_authorization')}` });

function DeleteCallModal({ call, onClose, onConfirm, deleting }) {
  return <div className="modal-overlay"><div className="modal-content">
    <span className="say-eyebrow">Call history</span><h2>Delete this call?</h2>
    <p>This permanently removes the call transcript and recording. This cannot be undone.</p>
    <div className="say-dialog-actions"><Button variant="secondary" type="button" onClick={onClose}>Cancel</Button><Button variant="danger" type="button" loading={deleting} loadingLabel="Deleting…" onClick={() => onConfirm(call.id)}>Delete call</Button></div>
  </div></div>;
}

function TranscriptsView({ orgMember }) {
  const isAdministrator = Boolean(orgMember?.is_org_admin || orgMember?.is_admin);
  const loadSequence = useRef(0);
  const [calls, setCalls] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [scope, setScope] = useState('all');
  const [readFilter, setReadFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [recordingFilter, setRecordingFilter] = useState('all');
  const [messageFilter, setMessageFilter] = useState('all');
  const [handlingFilter, setHandlingFilter] = useState('all');
  const [reminderFilter, setReminderFilter] = useState('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useSearchParams();
  const callIds = searchParams.getAll('callId');
  const selectedCallId = callIds.length === 0 ? null : callIds.length === 1 ? callIds[0] : '';
  const setSelectedCallId = useCallback(callId => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      if (callId === null) next.delete('callId');
      else next.set('callId', callId);
      return next;
    }, { replace: callId === null });
  }, [setSearchParams]);
  const [deletingCall, setDeletingCall] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const loadCalls = useCallback(async ({ quiet = false } = {}) => {
    const sequence = ++loadSequence.current;
    const revision = contentPreferenceRevision();
    const token = localStorage.getItem('jwt_authorization');
    const current = () => sequence === loadSequence.current && revision === contentPreferenceRevision() && token === localStorage.getItem('jwt_authorization');
    if (!quiet) setIsLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_HOST}/v2/calls`, {
        headers: authHeaders(),
        params: {
          limit: pageSize,
          offset: pageIndex * pageSize,
          search: debouncedSearch || undefined,
          status: statusFilter === 'all' ? undefined : statusFilter,
          recording: recordingFilter === 'all' ? undefined : recordingFilter,
          message: scope === 'all' && messageFilter !== 'all' ? messageFilter : undefined,
          handling: handlingFilter === 'all' ? undefined : handlingFilter,
          reminder: reminderFilter === 'all' ? undefined : reminderFilter,
          scope,
          read: scope === 'mine' && readFilter !== 'all' ? readFilter : undefined,
        },
      });
      if (!current()) return;
      const nextCalls = Array.isArray(response.data.calls) ? response.data.calls : [];
      const nextTotal = Number(response.data.total) || 0;
      const lastPage = Math.max(Math.ceil(nextTotal / pageSize) - 1, 0);
      if (pageIndex > lastPage) {
        setCalls([]);
        setPageIndex(lastPage);
      }
      else setCalls(nextCalls);
      setTotal(nextTotal);
      setUnreadTotal(Number(response.data.unreadTotal) || 0);
    }
    catch (error) {
      if (!current()) return;
      if ([401, 403].includes(error.response?.status)) {
        setCalls([]);
        setTotal(0);
        setUnreadTotal(0);
        if (error.response.status === 401) redirectToCallSignIn();
      }
      if (!quiet) flashMessage('error', 'Call history could not be loaded');
    }
    finally { if (current() && !quiet) setIsLoading(false); }
  }, [debouncedSearch, pageIndex, pageSize, readFilter, recordingFilter, messageFilter, handlingFilter, reminderFilter, scope, statusFilter]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPageIndex(0);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadCalls();
    const timer = window.setInterval(() => loadCalls({ quiet: true }), 3000);
    const unsubscribe = subscribeContentPreferences(() => { loadSequence.current += 1; setCalls([]); loadCalls(); });
    return () => { loadSequence.current += 1; unsubscribe(); window.clearInterval(timer); };
  }, [loadCalls]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_HOST}/v2/calls/recording-settings`, { headers: authHeaders() })
      .then(response => setSettings(response.data.settings)).catch(() => {});
  }, []);

  async function handleRemove(callId) {
    setIsDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_HOST}/v2/calls/${callId}`, { headers: authHeaders() });
      setDeletingCall(null);
      if (selectedCallId === callId) setSelectedCallId(null);
      if (calls.length === 1 && pageIndex > 0) setPageIndex(current => current - 1);
      else await loadCalls({ quiet: true });
      flashMessage('success', 'Call deleted');
    } catch (error) {
      flashMessage('error', error.response?.data?.error?.message || 'Call could not be deleted');
    } finally { setIsDeleting(false); }
  }

  async function handleReadState(callId, read) {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_HOST}/v2/calls/${callId}/read`,
        { read },
        { headers: authHeaders() },
      );
      setCalls(current => current.map(call => call.id === callId ? {
        ...call,
        messageAssignment: call.messageAssignment ? {
          ...call.messageAssignment,
          isRead: response.data.isRead,
          readAt: response.data.readAt,
        } : call.messageAssignment,
      } : call));
      setUnreadTotal(Number(response.data.unreadTotal) || 0);
      if (scope === 'mine' && readFilter !== 'all') await loadCalls({ quiet: true });
    } catch (error) {
      flashMessage('error', 'Message status could not be updated');
    }
  }

  const handleDrawerRead = useCallback(result => {
    setCalls(current => current.map(call => call.id === result.callId ? {
      ...call,
      messageAssignment: call.messageAssignment ? {
        ...call.messageAssignment,
        isRead: true,
        readAt: result.readAt,
      } : call.messageAssignment,
    } : call));
    setUnreadTotal(Number(result.unreadTotal) || 0);
    if (scope === 'mine' && readFilter === 'unread') loadCalls({ quiet: true });
  }, [loadCalls, readFilter, scope]);

  const pageCount = Math.max(Math.ceil(total / pageSize), 1);
  const handleHandlingChange = useCallback(() => loadCalls({ quiet: true }), [loadCalls]);
  const startRecord = calls.length ? pageIndex * pageSize + 1 : 0;
  const endRecord = calls.length ? pageIndex * pageSize + calls.length : 0;
  const canManageRecordings = isAdministrator;
  const showingInbox = scope === 'mine';
  const filterControls = [
    { key: 'reminder', label: 'Your reminders', ariaLabel: 'Filter your reminders', value: reminderFilter, setValue: setReminderFilter, options: [['all', 'Any reminder status'], ['due', 'Due'], ['upcoming', 'Upcoming']] },
    showingInbox
      ? { key: 'read', label: 'Read status', ariaLabel: 'Filter read status', value: readFilter, setValue: setReadFilter, options: [['all', 'Any read status'], ['unread', 'Unread'], ['read', 'Read']] }
      : { key: 'message', label: 'Message recipient', ariaLabel: 'Filter messages', value: messageFilter, setValue: setMessageFilter, options: [['all', 'All messages'], ['mine', 'For you'], ['general', 'General']] },
    { key: 'handling', label: 'Message handling', ariaLabel: 'Filter message handling', value: handlingFilter, setValue: setHandlingFilter, options: [['all', 'Any handling status'], ['needs_attention', 'Needs attention'], ['in_progress', 'In progress'], ['handled', 'Handled']] },
    { key: 'status', label: 'Call status', ariaLabel: 'Filter by status', value: statusFilter, setValue: setStatusFilter, options: [['all', 'All statuses'], ['completed', 'Completed'], ['in-progress', 'In progress'], ['failed', 'Failed'], ['no-answer', 'No answer']] },
    { key: 'recording', label: 'Recording', ariaLabel: 'Filter recordings', value: recordingFilter, setValue: setRecordingFilter, options: [['all', 'Any recording status'], ['with', 'With recording'], ['without', 'Without recording']] },
  ];
  const activeFilters = filterControls.filter(filter => filter.value !== 'all');
  const hasActiveFilters = search || activeFilters.length > 0;

  function clearFilters() {
    setMessageFilter('all');
    setReadFilter('all');
    setHandlingFilter('all');
    setReminderFilter('all');
    setStatusFilter('all');
    setRecordingFilter('all');
    setPageIndex(0);
  }

  return <>
    <Surface className="transcripts-section" padding="none">
      <div className="calls-list-heading">
        <div className="calls-heading-top">
          <div className="calls-list-title">
            <span className="say-eyebrow">{showingInbox ? 'Inbox' : 'Conversations'}</span>
            <small>{total} {showingInbox ? 'messages' : 'total calls'}{unreadTotal ? ` · ${unreadTotal} unread` : ''}</small>
          </div>
          {canManageRecordings && settings ? <Button className="calls-recording-settings" variant="secondary" size="sm" onClick={() => setShowSettings(true)}>Recording settings</Button> : null}
        </div>
        <div className="calls-toolbar">
          <div className="calls-scope-tabs" role="group" aria-label="Call history scope">
            <button className={scope === 'all' ? 'active' : ''} aria-pressed={scope === 'all'} type="button" onClick={() => { setScope('all'); setReadFilter('all'); setPageIndex(0); }}>All calls</button>
            <button className={scope === 'mine' ? 'active' : ''} aria-pressed={scope === 'mine'} type="button" onClick={() => { setScope('mine'); setPageIndex(0); }}>My messages{unreadTotal ? ` (${unreadTotal})` : ''}</button>
          </div>
          <div className="calls-list-actions">
            <input type="search" className="org-search-input" aria-label={showingInbox ? 'Search messages' : 'Search calls'} value={search} onChange={event => setSearch(event.target.value)} placeholder={showingInbox ? 'Search messages' : 'Search calls'} />
            <Button className="calls-filter-toggle" variant="secondary" size="sm" aria-expanded={filtersOpen} aria-controls="call-history-filters" onClick={() => setFiltersOpen(open => !open)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d="M4 7h9m4 0h3M4 17h3m4 0h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></svg>
              Filters{activeFilters.length ? <span className="calls-filter-count">{activeFilters.length}</span> : null}
            </Button>
          </div>
        </div>
        {filtersOpen ? <div className="calls-filter-panel" id="call-history-filters" role="region" aria-label="Call filters">
          {filterControls.map(filter => <label className="calls-filter-field" key={filter.key}>
            <span>{filter.label}</span>
            <select className="say-select" aria-label={filter.ariaLabel} value={filter.value} onChange={event => { filter.setValue(event.target.value); setPageIndex(0); }}>
              {filter.options.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
            </select>
          </label>)}
        </div> : null}
        {activeFilters.length ? <div className="calls-active-filters" role="group" aria-label="Active filters">
          {activeFilters.map(filter => {
            const valueLabel = filter.options.find(([value]) => value === filter.value)?.[1];
            return <button className="calls-filter-chip" type="button" key={filter.key} aria-label={`Remove ${filter.label.toLowerCase()} filter: ${valueLabel}`} onClick={() => { filter.setValue('all'); setPageIndex(0); }}>
              {filter.label}: {valueLabel}<span aria-hidden="true">×</span>
            </button>;
          })}
          <button className="calls-clear-filters" type="button" onClick={clearFilters}>Clear filters</button>
        </div> : null}
      </div>
      <div data-say-id="call-history" className="calls-transcript-list">
        {isLoading ? <div className="calls-list-loading"><Skeleton height={72} /><Skeleton height={72} /><Skeleton height={72} /></div> : calls.length ? calls.map(call => <CallHistoryItem key={call.id} call={call} onOpen={item => setSelectedCallId(item.id)} onDelete={canManageRecordings ? setDeletingCall : null} onToggleRead={call.messageAssignment?.assignedToYou ? handleReadState : null} />) : <EmptyState title={hasActiveFilters ? (showingInbox ? 'No matching messages' : 'No matching calls') : (showingInbox ? 'No messages assigned to you' : 'No calls yet')} description={hasActiveFilters ? 'Try changing your search or filters.' : showingInbox ? 'Messages addressed or routed to you will appear here.' : 'Once Sayable answers a call, its summary, outcomes, transcript, and recording will appear here.'} />}
      </div>
      {!isLoading && total > 0 ? <div className="say-table-footer">
        <div className="say-pagination-controls" role="group" aria-label="Call pages"><button className="say-pagination-button" onClick={() => setPageIndex(0)} disabled={pageIndex === 0}>First</button><button className="say-pagination-button" onClick={() => setPageIndex(current => Math.max(current - 1, 0))} disabled={pageIndex === 0}>Previous</button><button className="say-pagination-button" onClick={() => setPageIndex(current => Math.min(current + 1, pageCount - 1))} disabled={pageIndex >= pageCount - 1}>Next</button><button className="say-pagination-button" onClick={() => setPageIndex(pageCount - 1)} disabled={pageIndex >= pageCount - 1}>Last</button></div>
        <div className="say-pagination-summary">Showing {startRecord} to {endRecord} of {total} {showingInbox ? 'messages' : 'calls'}</div>
        <div className="say-page-size-control"><label htmlFor="callPageSize">Rows per page</label><select id="callPageSize" className="say-select" value={pageSize} onChange={event => { setPageSize(Number(event.target.value)); setPageIndex(0); }}><option value="10">10</option><option value="25">25</option><option value="50">50</option></select></div>
      </div> : null}
    </Surface>
    {selectedCallId !== null ? <CallDetailDrawer key={selectedCallId} callId={selectedCallId} onClose={() => setSelectedCallId(null)} onRead={handleDrawerRead} onHandlingChange={handleHandlingChange} /> : null}
    {deletingCall ? <DeleteCallModal call={deletingCall} deleting={isDeleting} onClose={() => setDeletingCall(null)} onConfirm={handleRemove} /> : null}
    {showSettings ? <RecordingSettingsModal initialSettings={settings} onClose={() => setShowSettings(false)} onSaved={setSettings} /> : null}
  </>;
}

export default TranscriptsView;
